import React, { useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";

/**
 * RichChatEditor – чат-редактор с:
 * - жирным/курсив/заголовки/списки/ссылки
 * - изображения (вставка из буфера, drag&drop, кнопкой)
 * - авто-ростом и мини-скроллом при переполнении
 * - Enter = отправка; Shift+Enter = новая строка
 *
 * props:
 *  - onSend(html, plainText): callback при отправке
 *  - className, placeholder
 */
export default function RichChatEditor({
  onSend,
  className = "",
  placeholder = "Zapros yozing…",
}) {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        history: true,
      }),
      Placeholder.configure({ placeholder }),
      Link.configure({
        autolink: true,
        openOnClick: true,
        protocols: ["http", "https", "mailto", "tel"],
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: "",
    editorProps: {
      attributes: {
        class:
          "tiptap-input" + (className ? ` ${className}` : ""),
        // отключаем spellcheck, если нужно:
        // spellcheck: "false",
      },
      handleDrop: (view, event) => {
        const hasFiles =
          event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length;
        if (!hasFiles) return false;

        const file = event.dataTransfer.files[0];
        if (!file.type.startsWith("image/")) return false;

        const reader = new FileReader();
        reader.onload = () => {
          editor.chain().focus().setImage({ src: reader.result }).run();
        };
        reader.readAsDataURL(file);
        event.preventDefault();
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type?.startsWith("image/")) {
            const file = item.getAsFile();
            const reader = new FileReader();
            reader.onload = () => {
              editor.chain().focus().setImage({ src: reader.result }).run();
            };
            reader.readAsDataURL(file);
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: () => {
      // тут можно синхронизировать состояние, если нужно
    },
  });

  if (!editor) return null;

  const send = () => {
    const html = editor.getHTML().trim();
    const text = editor.getText().trim();
    if (!text && !/<img /i.test(html)) return; // пусто
    onSend?.(html, text);
    editor.commands.clearContent();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="rce">
      {/* Toolbar */}
      <div className="rce-toolbar">
        <button
          type="button"
          className={editor.isActive("bold") ? "active" : ""}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Qalinlik"
        >B</button>
        <button
          type="button"
          className={editor.isActive("italic") ? "active" : ""}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Kursiv"
        ><i>I</i></button>
        <button
          type="button"
          className={editor.isActive("bulletList") ? "active" : ""}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Marketlangan ro'yxat"
        >• Ro'yxat</button>
        <button
          type="button"
          className={editor.isActive("orderedList") ? "active" : ""}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Raqamlangan ro'yxat"
        >1. Ro'yxat</button>
        <button
          type="button"
          onClick={() => {
            const url = prompt("Havolani kiriting:");
            if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          }}
          title="Havola"
        >Havola</button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          title="Tasvir"
        >🖼️</button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
              editor.chain().focus().setImage({ src: reader.result }).run();
            };
            reader.readAsDataURL(file);
            e.target.value = "";
          }}
        />
      </div>

      {/* Editor */}
      <div className="rce-input-wrap" onKeyDown={onKeyDown}>
        <EditorContent editor={editor} />
      </div>

      {/* Actions */}
      <div className="rce-actions">
        <button className="sendButton" onClick={send} title="Yuborish">
            <img src="https://cdn-icons-png.flaticon.com/128/3682/3682321.png" alt="" />
        </button>
      </div>
    </div>
  );
}
